import { useState } from 'react'
import { useQueries } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { PageHeader } from '@/components/page'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardDescription, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { ACTION_LABEL, actionTone, auditLogQueryOptions } from '@/features/audit/api'
import {
  PLATFORM_LABEL,
  PLATFORMS,
  useAppPatchPolicy,
  useMandatoryRules,
  type PatchPolicyItem,
  type Platform,
} from '@/features/app-patch-policy/api'
import { LocaleProvider, LocaleSwitcher, useLocale } from '@/features/app-patch-policy/locale'
import { FlagPanel } from '@/features/app-patch-policy/flag-panel'
import { PromotePanel } from '@/features/app-patch-policy/promote-panel'
import { RollbackSection } from '@/features/app-patch-policy/rollback-section'
import { localStamp } from '@/lib/datetime'
import { formatMb } from '@/lib/utils'

/** Just mounts the page-scoped language switch — see locale.tsx — above everything else so
 * AppPatchPolicyPageContent (and every card below it) can call useLocale(). */
export function AppPatchPolicyPage() {
  return (
    <LocaleProvider>
      <AppPatchPolicyPageContent />
    </LocaleProvider>
  )
}

/**
 * An "I want to…" action picker (Ship a patch / Change mandatory-optional flag / Emergency
 * rollback) — one panel shown at a time instead of stacked top to bottom. Each panel lives in
 * its own file: promote-panel.tsx, flag-panel.tsx (+ mandatory-rules-card.tsx /
 * native-version-card.tsx), rollback-section.tsx.
 */
function AppPatchPolicyPageContent() {
  const { t } = useLocale()
  const { data, isPending, isError, error, refetch } = useAppPatchPolicy()

  return (
    <>
      <PageHeader title={t('page.title')} actions={<LocaleSwitcher />} />
      {isPending && (
        <div className="grid gap-4 sm:grid-cols-2">
          <Card>
            <Skeleton className="h-52 w-full rounded-xl" />
          </Card>
          <Card>
            <Skeleton className="h-52 w-full rounded-xl" />
          </Card>
        </div>
      )}
      {isError && (
        <Card>
          <CardTitle>{t('page.loadErrorTitle')}</CardTitle>
          <CardDescription className="mt-2">{(error as Error).message}</CardDescription>
          <Button variant="secondary" size="sm" className="mt-4" onClick={() => void refetch()}>
            {t('common.tryAgain')}
          </Button>
        </Card>
      )}
      {!isPending && !isError && (
        <>
          <CardDescription className="mb-6">{t('page.intro')}</CardDescription>
          <StatusSummaryBar data={data} />
          <ActionWorkspace data={data} />
          <PatchRecordSection />
        </>
      )}
    </>
  )
}

/** At-a-glance per-platform status, shown before picking an action. The mandatory badge only
 * reflects whether *any* floor rule currently exists for the platform — see MandatoryRulesCard
 * for the actual per-release_version list. */
function StatusSummaryBar({ data }: { data: PatchPolicyItem[] }) {
  return (
    <div className="mb-6 grid gap-3 sm:grid-cols-2">
      {PLATFORMS.map((platform) => (
        <PlatformStatusCard key={platform} platform={platform} item={data.find((row) => row.platform === platform)} />
      ))}
    </div>
  )
}

function PlatformStatusCard({ platform, item }: { platform: Platform; item?: PatchPolicyItem }) {
  const { t } = useLocale()
  const rules = useMandatoryRules(platform)
  const hasMandatory = (rules.data?.length ?? 0) > 0

  return (
    <Card className="flex flex-row items-center justify-between gap-3">
      <div>
        <p className="text-[10px] font-bold uppercase tracking-wider text-faint">
          {PLATFORM_LABEL[platform]} · stable
        </p>
        <p className="mt-1 font-mono text-lg font-semibold">
          {item?.latest_patch_number != null
            ? `${item.latest_release_version ? `${item.latest_release_version} · ` : ''}patch ${item.latest_patch_number}`
            : '—'}
        </p>
        <p className="mt-0.5 text-xs text-faint">
          {item?.latest_patch_number != null
            ? item.latest_patch_size_bytes != null
              ? `${formatMb(item.latest_patch_size_bytes)} MB`
              : ''
            : t('platform.notSyncedYet')}
        </p>
      </div>
      {rules.isSuccess && (
        <Badge variant={hasMandatory ? 'destructive' : 'default'}>
          {hasMandatory ? t('summary.mandatoryOn') : t('summary.mandatoryOff')}
        </Badge>
      )}
    </Card>
  )
}

// The shared Tabs component's active state is shadow-only, which blends into this page's plain
// white background — scoped here rather than changing the shared component other pages use fine.
const ACTIVE_TAB_BORDER = 'data-[state=active]:border data-[state=active]:border-border-strong'

type ActionMode = 'promote' | 'flag' | 'roll'

function ActionWorkspace({ data }: { data: PatchPolicyItem[] }) {
  const { t } = useLocale()
  const [mode, setMode] = useState<ActionMode>('promote')

  return (
    <div className="mb-8">
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <span className="text-xs font-semibold text-faint">{t('actions.heading')}</span>
        <Tabs value={mode} onValueChange={(value) => setMode(value as ActionMode)}>
          <TabsList>
            <TabsTrigger value="promote" className={ACTIVE_TAB_BORDER}>
              {t('actions.promote')}
            </TabsTrigger>
            <TabsTrigger value="flag" className={ACTIVE_TAB_BORDER}>
              {t('actions.flag')}
            </TabsTrigger>
            <TabsTrigger value="roll" className={ACTIVE_TAB_BORDER}>
              {t('actions.roll')}
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {mode === 'promote' && <PromotePanel />}
      {mode === 'flag' && <FlagPanel />}
      {mode === 'roll' && <RollbackSection data={data} />}
    </div>
  )
}

/** This page's own slice of the Audit Log — one query per action (the filter takes only one),
 * fanned out via useQueries. "View full audit log" links to the real page for everything else. */
const PATCH_RECORD_ACTIONS = [
  'shorebird_patch_promoted',
  'shorebird_patch_rolled_back',
  'shorebird_patch_rolled_forward',
  'app_patch_mandatory_rule_set',
  'app_patch_mandatory_rule_cleared',
  'app_patch_mandatory_rule_lowered',
  'app_patch_policy_updated',
  'app_native_version_policy_updated',
] as const

function usePatchRecordRows() {
  const queries = useQueries({
    queries: PATCH_RECORD_ACTIONS.map((action) => auditLogQueryOptions({ action, pageSize: 6 })),
  })

  const rows = queries
    .flatMap((q) => q.data?.list ?? [])
    .sort((a, b) => (a.created_at < b.created_at ? 1 : -1))
    .slice(0, 8)

  return {
    isPending: queries.some((q) => q.isPending),
    isError: queries.every((q) => q.isError),
    rows,
  }
}

function PatchRecordSection() {
  const { t } = useLocale()
  const { isPending, isError, rows } = usePatchRecordRows()

  return (
    <Card className="mt-8">
      <div className="mb-3 flex items-center justify-between gap-3">
        <CardTitle>{t('record.heading')}</CardTitle>
        <Button variant="ghost" size="sm" asChild>
          <Link to="/audit">{t('record.viewAll')}</Link>
        </Button>
      </div>

      {isPending && <Skeleton className="h-24 w-full rounded-md" />}
      {isError && <p className="text-xs text-destructive">{t('record.loadError')}</p>}
      {!isPending && !isError && rows.length === 0 && (
        <p className="text-xs text-muted-foreground">{t('record.empty')}</p>
      )}
      {rows.length > 0 && (
        <ul className="space-y-1.5">
          {rows.map((row) => (
            <li
              key={row.id}
              className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border bg-muted/20 px-3 py-2 text-xs"
            >
              <span className="flex items-center gap-2">
                <Badge variant={actionTone(row.action)}>{ACTION_LABEL[row.action] ?? row.action}</Badge>
                <span className="text-muted-foreground">{row.target_desc}</span>
              </span>
              <span className="text-faint">
                {row.actor_name} · {localStamp(row.created_at)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  )
}
